import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './CaseStudyPage.css';

const QuantitativeTrading = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('');
    const [readingProgress, setReadingProgress] = useState(0);

    // Contents (Results first)
    const tableOfContents = useMemo(() => [
        { id: 'results', title: 'Results & Impact' },
        { id: 'technologies', title: 'Technologies Used' },
        { id: 'overview', title: 'Project Overview' },
        { id: 'challenge', title: 'The Challenge' },
        { id: 'solution', title: 'Solution & Implementation' }
    ], []);

    useEffect(() => {
        const handleScroll = () => {
            const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
            const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrolled = height > 0 ? (winScroll / height) * 100 : 0;
            setReadingProgress(scrolled);

            const sections = tableOfContents
                .map(item => document.getElementById(item.id))
                .filter(Boolean);

            const scrollPosition = window.scrollY + 200;
            for (let i = sections.length - 1; i >= 0; i--) {
                if (sections[i] && sections[i].offsetTop <= scrollPosition) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [tableOfContents]);

    const handleBackToCaseStudies = () => {
        navigate('/');
        setTimeout(() => {
            const mycaseSection = document.getElementById('mycase');
            if (mycaseSection) {
                mycaseSection.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
        }, 100);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="case-study-page">
            {/* Reading progress */}
            <div className="reading-progress-container">
                <div
                    className="reading-progress-bar"
                    style={{ height: `${readingProgress}%` }}
                ></div>
            </div>

            {/* Simple TOC */}
            <div className="table-of-contents">
                <h4>Contents</h4>
                <ul>
                    {tableOfContents.map((item, idx) => (
                        <li key={item.id || idx}>
                            <button
                                className={`toc-item ${activeSection === item.id ? 'active' : ''}`}
                                onClick={() => scrollToSection(item.id)}
                            >
                                {item.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="case-study-container">
                {/* Header */}
                <div className="case-study-header">
                    {/* Breadcrumb */}
                    <nav className="breadcrumb">
                        <button className="breadcrumb-item" onClick={() => navigate('/')}>
                            <HomeOutlined /> Home
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <button className="breadcrumb-item" onClick={handleBackToCaseStudies}>
                            Case Studies
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">Quantitative Research & Trading Strategies</span>
                    </nav>

                    <button className="back-button" onClick={handleBackToCaseStudies}>
                        <ArrowLeftOutlined /> Back to Case Studies
                    </button>

                    <div className="case-study-hero">
                        <div className="case-study-image">
                            <img src="/images/case/stock.jpg" alt="Quantitative Research & Trading Strategies" />
                        </div>

                        <div className="case-study-info">
                            <h1 className="case-study-title">Quantitative Research & Trading Strategies</h1>
                            <p className="case-study-subtitle">Time-Series Modelling • Statistical Arbitrage • Economic Relationships</p>

                            <div className="case-study-meta">
                                <div className="meta-item">
                                    <span className="meta-label">Duration:</span>
                                    {/* Resume: 2020/09–2022/08 ⇒ ≈ 24 months */}
                                    <span className="meta-value">~24 months</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Focus:</span>
                                    <span className="meta-value">
                                        U.S. equity arbitrage; gold–oil price dynamics; large-scale time-series analysis
                                    </span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Status:</span>
                                    <span className="meta-value status-production">
                                        Research & Backtesting • Contribution indexed in EconLit
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="case-study-content">
                    {/* Results & Impact */}
                    <section className="content-section" id="results">
                        <h2>Results &amp; Impact</h2>
                        <p>
                            Built and iterated systematic strategies with rigorous <strong>statistical hypothesis testing</strong>,
                            <strong> cointegration analysis</strong>, <strong>threshold regression</strong>, and disciplined <strong>backtesting</strong>.
                            Automated market data ingestion and <strong>real-time signal generation scripts</strong> in Python; findings informed a paper
                            indexed in <strong>EconLit</strong>.
                        </p>

                        <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">12%</div>
                                <div className="result-label">Annualised (Arbitrage Backtest)</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Validated</div>
                                <div className="result-label">Gold–Oil Relationship via Threshold Regression</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Analyst</div>
                                <div className="result-label">Data Ingestion & Signals (Python)</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Indexed</div>
                                <div className="result-label">Research Contribution (EconLit)</div>
                            </div>
                        </div>

                        {/* Mobile Results */}
                        <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">12%</div>
                                </div>
                                <div className="mobile-result-label">Annualised Backtest</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Validated</div>
                                </div>
                                <div className="mobile-result-label">Gold–Oil Model</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Automated</div>
                                </div>
                                <div className="mobile-result-label">ETL + Signals</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Indexed</div>
                                </div>
                                <div className="mobile-result-label">EconLit</div>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Technologies */}
                    <section className="content-section" id="technologies">
                        <h2>Technologies Used</h2>
                        <div className="tech-stack">
                            <span className="tech-tag">Python (Data Ingestion & Signals)</span>
                            <span className="tech-tag">R (Time-Series & Econometrics)</span>
                            <span className="tech-tag">Statistical Hypothesis Testing</span>
                            <span className="tech-tag">Cointegration Analysis</span>
                            <span className="tech-tag">Threshold Regression</span>
                            <span className="tech-tag">Backtesting Frameworks</span>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Project Overview */}
                    <section className="content-section" id="overview">
                        <h2>Project Overview</h2>
                        <p>
                            Conducted in-depth quantitative research across two streams: <strong>statistical arbitrage in U.S. equities</strong>
                            and <strong>gold–oil price dynamics</strong>. Scope covered end-to-end data work—cleaning, transformation,
                            exploratory analysis, modelling, and strategy backtesting—using Python and R to handle large-scale time-series datasets.
                        </p>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* The Challenge */}
                    <section className="content-section" id="challenge">
                        <h2>The Challenge</h2>
                        <p>
                            Key challenges included <strong>non-stationarity</strong> and <strong>structural breaks</strong> in commodity relationships,
                            <strong> noise vs. signal separation</strong> in high-frequency equity data, and avoiding <strong>look-ahead</strong> / <strong>survivorship bias</strong>
                            in backtests. Additionally, integrating <strong>transaction costs</strong> into strategy evaluation was essential for realistic results.
                        </p>
                        <div className="challenge-grid">
                            <div className="challenge-item">
                                <h4>Data Quality & Transformations</h4>
                                <p>Cleaning, alignment, missing data handling across heterogeneous sources.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Time-Series Properties</h4>
                                <p>Dealing with non-stationarity, volatility clustering, and regime shifts.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Bias Control in Backtests</h4>
                                <p>Preventing look-ahead and survivorship bias; modelling realistic frictions.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Signal Robustness</h4>
                                <p>Ensuring generalisation beyond specific windows / assets.</p>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Solution & Implementation */}
                    <section className="content-section" id="solution">
                        <h2>Solution &amp; Implementation</h2>
                        <p>
                            Implemented a reproducible research pipeline spanning data ingestion, feature engineering, model calibration,
                            and backtesting. Emphasis was on <strong>econometric validity</strong> (hypothesis testing, cointegration) and
                            <strong> regime-aware modelling</strong> (threshold regression) to capture structural changes.
                        </p>

                        <div className="solution-features">
                            <div className="feature-item">
                                <h4>Automated Data Ingestion (Python)</h4>
                                <p>Scripted API fetchers and schedulers; normalised and timestamp-aligned multi-asset feeds.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Exploratory & Hypothesis Testing (R)</h4>
                                <p>Large-scale EDA with formal tests to verify assumptions prior to modelling.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Cointegration-Based Strategy Design</h4>
                                <p>Identified candidate pairs/relationships; encoded mean-reversion entry/exit logic.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Threshold Regression for Commodities</h4>
                                <p>Captured regime-dependent gold–oil dynamics; located structural breakpoints.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Backtesting & Evaluation</h4>
                                <p>Simulated strategies with realistic costs/latency; reported annualised return and drawdown metrics.</p>
                            </div>
                            <div className="feature-item">
                                <h4>Real-time Signal Scripts</h4>
                                <p>Generated actionable signals from live data as a downstream of the research pipeline.</p>
                            </div>
                        </div>

                        {/* Section Divider */}
                        <div className="section-divider"></div>

                        <h2>Results &amp; Impact</h2>
                        <p>
                            The research produced a <strong>12% annualised</strong> backtested return for the arbitrage strategy and a validated
                            <strong> threshold-regression model</strong> for gold–oil price dynamics. The methodology, code, and findings supported a publication
                            indexed in <strong>EconLit</strong>, evidencing academic rigour and practical transferability to quantitative roles.
                        </p>

                        {/* <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">12%</div>
                                <div className="result-label">Annualised (Strategy Backtest)</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Regimes</div>
                                <div className="result-label">Breakpoints Identified</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Signals</div>
                                <div className="result-label">Real-time Generation (Python)</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">EconLit</div>
                                <div className="result-label">Indexed Contribution</div>
                            </div>
                        </div>

                        <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">12%</div>
                                </div>
                                <div className="mobile-result-label">Annualised</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Regimes</div>
                                </div>
                                <div className="mobile-result-label">Breakpoints</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Signals</div>
                                </div>
                                <div className="mobile-result-label">Real-time</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">EconLit</div>
                                </div>
                                <div className="mobile-result-label">Indexed</div>
                            </div>
                        </div> */}
                    </section>

                    {/* Back to Case Studies at the end */}
                    <div style={{ textAlign: 'left', margin: '0px 0', paddingTop: '20px', borderTop: '1px solid #e8e8e8' }}>
                        <button className="back-button" onClick={handleBackToCaseStudies}>
                            <ArrowLeftOutlined /> Back to Case Studies
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuantitativeTrading;
